import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  SendFlatInterestRequest,
  GetTenantInterestRequests,
} from "../../../redux/reducer/FlatInterestRequest";
import { ValidateUser } from "../../../redux/reducer/ValidateUser";
import {
  evaluateTenantEligibility,
  tenantProfileFromUser,
} from "../../../utils/tenantEligibility";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "500px" },
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  overflow: "hidden",
};

export default function InterestRequestForm({
  open,
  onClose,
  flatData,
  landlordId,
  landlordName,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const { validateUser } = useSelector((state) => state);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    moveInDate: dayjs().add(7, "day"),
    familySize: "",
    occupation: "",
    message: "",
    phoneNumber: "",
  });

  // Tenant info: the Redux copy (fetched fresh via ValidateUser on every
  // login/app load) is the source of truth — localStorage is only a
  // fallback for the brief window before that fetch resolves. Reading
  // localStorage alone here is what left tenantEmail blank right after a
  // fresh registration+login, even though the same screen already trusts
  // Redux for phone/family size/occupation just below.
  const currentUser = validateUser?.UserValidate?.data?.user;
  const tenantId =
    currentUser?._id ||
    localStorage.getItem("user_id") ||
    localStorage.getItem("userId") ||
    sessionStorage.getItem("user_id") ||
    "";
  const tenantName =
    currentUser?.name ||
    localStorage.getItem("name") ||
    sessionStorage.getItem("name") ||
    "";
  const tenantEmail =
    currentUser?.email ||
    localStorage.getItem("user_email") ||
    sessionStorage.getItem("user_email") ||
    "";

  // Last line of defence: the card and the detail modal already disable the
  // request, so reaching this form ineligible means something went stale — the
  // request must still not go out.
  const eligibility = evaluateTenantEligibility(
    flatData,
    tenantProfileFromUser(validateUser)
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async () => {
    if (!eligibility.canRequest) {
      setError(eligibility.blockers.map((rule) => rule.reason).join(" "));
      return;
    }

    // Validation
    // if (!formData.familySize) {
    //   setError("Please select family size");
    //   return;
    // }
    // if (!formData.phoneNumber) {
    //   setError("Please enter your phone number");
    //   return;
    // }

    // Check required data
    if (!tenantId) {
      setError("Please login again. Session expired.");
      return;
    }
    if (!landlordId) {
      setError("Landlord information missing. Please try again.");
      return;
    }
    if (!formData.moveInDate || !formData.moveInDate.isValid?.()) {
      setError("Please select a valid move-in date.");
      return;
    }

    setLoading(true);
    setError("");

    // Get flat ID (could be _id, id, or flatId)
    const flatId = flatData?._id || flatData?.id || flatData?.flatId;

    const requestData = {
      // Tenant Info
      tenantId: tenantId,
      tenantName: tenantName,
      tenantEmail: tenantEmail,
      tenantPhone: validateUser?.UserValidate?.data?.user?.contactNumber,

      // Flat Info
      flatId: flatId,
      flatName: flatData?.flatName || "",
      flatRent: flatData?.flatRent || 0,
      flatCity: flatData?.city || "",
      flatAddress: flatData?.propertyAddress || "",
      flatAdvance: flatData?.flatAdvance || 0,

      // Landlord Info
      landlordId: landlordId,
      landlordName: landlordName || "",

      // Request Details from form
      moveInDate: formData.moveInDate.format("DD-MM-YYYY"),
      familySize:
        validateUser?.UserValidate?.data?.tenantProfile?.totalFamilyMembers,
      occupation:
        validateUser?.UserValidate?.data?.tenantProfile?.employmentStatus,
      // occupation: formData.occupation,
      message: formData.message,
    };

    try {
      const result = await dispatch(SendFlatInterestRequest({ requestData }));

      if (result?.payload?.data?.success || result?.payload?.status === 200) {
        setSuccess(true);
        dispatch(ValidateUser({}));
        if (tenantId) {
          dispatch(GetTenantInterestRequests({ tenantId }));
        }

        // Wait a moment to show success, then proceed
        setTimeout(() => {
          onSuccess();
        }, 1500);

      } else {
        setError(
          result?.error?.message ||
            "Failed to send request. Please try again."
        );
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      moveInDate: dayjs().add(7, "day"),
      familySize: "",
      occupation: "",
      message: "",
      phoneNumber: "",
    });
    setSuccess(false);
    setError("");
    onClose();
  };

  if (!flatData) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #eee",
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Send Interest Request
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
          {success ? (
            // Success State
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckCircleIcon
                sx={{ fontSize: 80, color: "success.main", mb: 2 }}
              />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Request Sent Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your interest request has been sent to {landlordName}. Owner
                will review your request and get back to you soon.
              </Typography>
              <Button variant="contained" onClick={handleClose}>
                Done
              </Button>
            </Box>
          ) : (
            <>
              {/* Flat Summary */}
              <Paper
                elevation={0}
                sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2, mb: 3 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Requesting for
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {flatData.flatName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {flatData.mapAddress || flatData.city}
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight={600}
                  sx={{ mt: 1 }}
                >
                  Rs {Number(flatData.flatRent || 0).toLocaleString()}/month
                </Typography>
              </Paper>

              {!eligibility.canRequest && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>This property does not match your profile.</strong>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {eligibility.blockers.map((rule) => (
                      <li key={rule.key}>
                        <Typography variant="body2" component="span">
                          {rule.reason}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Form */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Move-in Date */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Preferred Move-in Date"
                    value={formData.moveInDate}
                    onChange={(newValue) =>
                      handleChange("moveInDate", newValue)
                    }
                    minDate={dayjs()}
                    format="DD-MM-YYYY"
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </LocalizationProvider>

                {/* Family Size */}
                {/* <FormControl fullWidth required>
                  <InputLabel>Family Size / Occupants</InputLabel>
                  <Select
                    value={formData.familySize}
                    label="Family Size / Occupants"
                    onChange={(e) => handleChange("familySize", e.target.value)}
                  >
                    <MenuItem value="1">1 Person (Single)</MenuItem>
                    <MenuItem value="2">2 People (Couple)</MenuItem>
                    <MenuItem value="3">3 People</MenuItem>
                    <MenuItem value="4">4 People</MenuItem>
                    <MenuItem value="5">5 People</MenuItem>
                    <MenuItem value="6+">6+ People</MenuItem>
                  </Select>
                </FormControl> */}

                {/* Occupation */}
                {/* <FormControl fullWidth>
                  <InputLabel>Occupation</InputLabel>
                  <Select
                    value={formData.occupation}
                    label="Occupation"
                    onChange={(e) => handleChange("occupation", e.target.value)}
                  >
                    <MenuItem value="employed">Employed (Job)</MenuItem>
                    <MenuItem value="business">Business Owner</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="freelancer">Freelancer</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl> */}

                {/* Phone Number */}
                {/* <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  placeholder="03XX-XXXXXXX"
                  value={formData.phoneNumber}
                  onChange={(e) => handleChange("phoneNumber", e.target.value)}
                /> */}

                {/* Message */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Message to Landlord (Optional)"
                  placeholder="Introduce yourself, mention any questions or special requirements..."
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  inputProps={{ maxLength: 50 }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Your Info Summary */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Your Information (from profile)
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {tenantName}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {tenantEmail}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> 03
                  {validateUser?.UserValidate?.data?.user?.contactNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>Family Size / Occupants:</strong>{" "}
                  {
                    validateUser?.UserValidate?.data?.tenantProfile
                      ?.totalFamilyMembers
                  }
                </Typography>
              </Box>

              {/* Submit Button */}
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleSubmit}
                disabled={loading || !eligibility.canRequest}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SendIcon />
                }
                sx={{ py: 1.5, mt: 1 }}
              >
                {loading
                  ? "Sending..."
                  : !eligibility.canRequest
                  ? "Can't Send Request"
                  : "Send Interest Request"}
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2, textAlign: "center" }}
              >
                By sending this request, the owner will be able to see your
                profile information.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
